import React, { useEffect, useState } from "react";
import DataTable, { SortOrder } from "react-data-table-component";
import { withTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Form, Input } from "reactstrap";
import { Loader } from "src/components";
import { getPaginationRequestInitialValue, PaginationRequest } from "src/sdk";
import { Ticket } from "src/sdk/models/Ticket";
import { TicketService } from "src/services/TicketService";
import Swal from "sweetalert2";
import { TicketingSuperAdminColumns } from "./TicketingSuperAdminColumns";
import TicketingSuperAdminDetails from "./TicketingSuperAdminDetails";

const service = new TicketService();

export const TicketingSuperAdmin: React.FC = () => {
    const history = useHistory();

    const [ticketsData, setTicketsData] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
    const [modal_xlarge, setmodal_xlarge] = useState(false);

    const [paginationResetDefaultPage, setPaginationResetDefaultPage] = useState(false);
    const [totalRows, setTotalRows] = useState(10000);
    const [perPage, setPerPage] = useState(50);
    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState<string>("");
    const [search, setSearch] = useState<string>("");
    const [sortBy, setSortBy] = useState<string>("");
    const [sortOrder, setSortOrder] = useState<string>("");

    const tog_xlarge = () => setmodal_xlarge(!modal_xlarge);

    const handleDetailsClick = (ticket: Ticket) => {
        setSelectedTicket(ticket);
        setmodal_xlarge(true);
    };

    const handleAddTicket = () => {
        history.push("/admin-create-ticket");
    };

    const fetchPage = async (page: any, search: any, paginationResetDefaultPage: any, sortBy: any, sortOrder: any) => {
        setLoading(true);

        let pagination: PaginationRequest = getPaginationRequestInitialValue();
        pagination.page = page;
        pagination.pageSize = perPage;
        pagination.search = search;
        pagination.sortBy = sortBy;
        pagination.sortOrder = sortOrder;

        getTickets(setTicketsData, pagination, setTotalRows, setLoading);
        setPaginationResetDefaultPage(paginationResetDefaultPage);

    };

    useEffect(() => {
        fetchPage(page, search, false, sortBy, sortOrder);
    }, [page, perPage, search]);

    const handlePageChange = (pageNew: number) => {
        fetchPage(pageNew, search, false, sortBy, sortOrder);
    };

    const handlePerRowsChange = (newPerPage: number, page: number) => {
        setLoading(true);
        let pagination: PaginationRequest = getPaginationRequestInitialValue();
        pagination.page = page;
        pagination.pageSize = newPerPage;
        pagination.search = search;
        pagination.sortBy = sortBy;
        pagination.sortOrder = sortOrder;

        getTickets(setTicketsData, pagination, setTotalRows, setLoading);
        setPerPage(newPerPage);
    };

    const sortPagination = (state: any, sortDirection: SortOrder) => {
        setSortOrder(sortDirection);
        setSortBy(state.id);
        fetchPage(page, search, false, state.id, sortDirection);
    };

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        setSearch(searchInput);
    };

    const paginationComponentOptions = {
        rowsPerPageText: "Rows per page",
        rangeSeparatorText: "of",
        selectAllRowsItem: false,
    };

    const handleDeleteClick = (ticket: Ticket) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                service.deleteTicket(ticket.id as number)
                    .then(() => {
                        Swal.fire('Deleted!', 'The ticket has been deleted.', 'success');
                        fetchPage(1, '', false, "", "");
                    })
                    .catch((error) => {
                        console.error("API ERROR", error);
                        Swal.fire('Error', 'There was a problem deleting the ticket.', 'error');
                    });
            }
        });
    };

    return (
        <React.Fragment>
            <div className="container-fluid mt-4">
                <div className="mb-4 d-flex justify-content-between align-items-center">
                    <h3>Ticketing</h3>
                </div>

                <div className="d-flex mb-3">
                    <div className="me-auto w-50">
                        <Form onSubmit={handleSearchSubmit}>
                            <div className="form-group m-0">
                                <div className="input-group">
                                    <Input
                                        type="text"
                                        placeholder="Search..."
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                    />
                                    <div className="input-group-append">
                                        <button
                                            className="btn btn-secondary rounded-0 rounded-end bi bi-search"
                                            type="submit"
                                        />
                                    </div>
                                </div>
                            </div>
                        </Form>
                    </div>
                </div>

                {/* Table */}
                <div className="table-responsive">

                    <DataTable
                        data={ticketsData}
                        columns={TicketingSuperAdminColumns(handleDetailsClick, handleDeleteClick)}
                        customStyles={customStyles}
                        progressPending={loading}
                        progressComponent={<Loader loading={loading} />}
                        //////
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationResetDefaultPage={paginationResetDefaultPage}
                        onChangeRowsPerPage={handlePerRowsChange}
                        pointerOnHover={true}
                        onChangePage={handlePageChange}
                        paginationComponentOptions={paginationComponentOptions}
                        paginationRowsPerPageOptions={[50, 100]}
                        onSort={sortPagination}
                        defaultSortAsc={false}
                        highlightOnHover
                        striped
                        responsive
                    />
                </div>
            </div>

            {selectedTicket && (
                <TicketingSuperAdminDetails
                    id={selectedTicket.id as number}
                    modal_xlarge={modal_xlarge}
                    setmodal_xlarge={setmodal_xlarge}
                    tog_xlarge={tog_xlarge}
                />
            )}
        </React.Fragment>
    );
};

export default withTranslation()(TicketingSuperAdmin);

export const getTickets = (setTicketsData: any, pagination: any, setTotalRows: any, setLoading: any) => {
    service.getTickets(pagination)
        .then(function (response) {
            console.log(response);
            setTotalRows(response.data.totalRecords);
            setTicketsData(response.data.data);
        })
        .catch(function (error) {
            console.log("API ERROR");
            console.log(error);
        })
        .finally(() => {
            setLoading(false);
        });
}

const customStyles = {
    headRow: {
        style: {
            fontSize: 12,
            height: "40px",
            minHeight: "40px",
        },
    },
    headCells: {
        style: {
            width: "10px",
            borderLeft: `1px solid #dee2e6`,
            borderRight: `1px solid #dee2e6`,
            backgroundColor: "#274755",
            color: "#FFFFFF",
            height: "40px",
            maxHeight: "40px",
            fontWeight: 600,
        },
    },
    rows: {
        style: {
            borderBottomWidth: "0px !important",
            minHeight: "50px",
        },
    },
    cells: {
        style: {
            width: "10px",
            height: "50px",
            fontSize: 12,
            border: "1px solid #dee2e6",
            fontWeight: "bold",
            paddingLeft: "8px",
            paddingRight: "8px",
        },
    },
    pagination: {
        style: {
            fontSize: 13,
            minHeight: "56px",
            borderTop: "1px solid #dee2e6",
            paddingTop: "8px",
        },
    },
};
